/**
 * Modal de pré-visualização da NF-e antes de emitir.
 */

import { useState, useEffect } from 'react'
import { FileText, User, Package, DollarSign, AlertCircle, Loader2 } from 'lucide-react'
import { nfeService } from '../../../services/erpApi'
import ModalERP from '../ModalERP'
import './NFePreviewModal.css'

function NFePreviewModal({
  isOpen,
  onClose,
  saleId,
  quoteId,
  customerId,
  payments = [],
  isFromQuote = false,
  onConfirm,
  confirmLabel = 'Confirmar e transmitir',
  confirming = false,
  layer = 1
}) {
  const [previewData, setPreviewData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) {
      setPreviewData(null)
      setError(null)
      return
    }

    if ((saleId || quoteId) && customerId && payments.length > 0) {
      loadPreview()
    } else {
      setError('Dados incompletos para pré-visualização')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, saleId, quoteId, customerId])

  const loadPreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await nfeService.preview({
        saleId,
        quoteId,
        customerId,
        payments
      })

      if (response.success) {
        setPreviewData(response.data)
      } else {
        setError(response.message || 'Erro ao gerar pré-visualização')
      }
    } catch (err) {
      console.error('Erro ao carregar pré-visualização:', err)
      setError(err.response?.data?.message || 'Erro ao gerar pré-visualização')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  return (
    <ModalERP
      isOpen={isOpen}
      onClose={onClose}
      title="Pré-visualização da NF-e"
      subtitle="Revise os dados antes de transmitir à SEFAZ"
      icon={<FileText size={22} />}
      size="xl"
      layer={layer}
      loading={loading || confirming}
      footer={
        <>
          <button
            type="button"
            className="modal-erp-btn-secondary"
            onClick={onClose}
            disabled={confirming}
          >
            Voltar
          </button>
          {onConfirm && previewData && !error && (
            <button
              type="button"
              className="modal-erp-btn-primary"
              onClick={onConfirm}
              disabled={confirming || loading}
            >
              {confirming && <Loader2 size={16} className="modal-erp-spinner" />}
              {confirmLabel}
            </button>
          )}
        </>
      }
    >
      <div className="nfe-preview-modal">
        {loading ? (
          <div className="loading">Gerando pré-visualização...</div>
        ) : error ? (
          <div className="error-state">
            <AlertCircle size={48} />
            <p>{error}</p>
          </div>
        ) : previewData ? (
          <>
            <div className="preview-summary">
              <div className="summary-card">
                <div className="summary-header">
                  <User size={20} />
                  <h3>Cliente</h3>
                </div>
                <div className="summary-content">
                  <p><strong>Nome:</strong> {previewData.cliente?.name || '-'}</p>
                  <p><strong>CPF/CNPJ:</strong> {previewData.cliente?.cpf_cnpj || '-'}</p>
                  {previewData.cliente?.email && (
                    <p><strong>E-mail:</strong> {previewData.cliente.email}</p>
                  )}
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-header">
                  <Package size={20} />
                  <h3>Itens</h3>
                </div>
                <div className="summary-content">
                  <p><strong>Quantidade:</strong> {previewData.itens?.length || 0} item(ns)</p>
                  <p><strong>Valor Total:</strong> {formatCurrency(previewData.total)}</p>
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-header">
                  <DollarSign size={20} />
                  <h3>Pagamentos</h3>
                </div>
                <div className="summary-content">
                  {payments.map((payment, index) => (
                    <p key={index}>
                      <strong>{payment.paymentMethodName || payment.name || 'Pagamento'}:</strong>{' '}
                      {formatCurrency(payment.amount)}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            <div className="preview-payload">
              <div className="payload-header">
                <FileText size={20} />
                <h3>Payload JSON (dados enviados à Focus)</h3>
              </div>
              <div className="payload-content">
                <pre>{JSON.stringify(previewData.payload, null, 2)}</pre>
              </div>
            </div>

            {isFromQuote && (
              <div className="preview-note">
                <AlertCircle size={20} />
                <p>O orçamento será convertido automaticamente em venda ao emitir a NF-e.</p>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>Nenhum dado disponível para pré-visualização</p>
          </div>
        )}
      </div>
    </ModalERP>
  )
}

export default NFePreviewModal
